import { useState, useEffect } from "react";
import { establishTwilioDevice } from "../utils/twilio-utils";
import { validateNumber, formattedUSPhoneNumber } from "components/dialer/utils/dialer-utils";
import EverTrue from "app";
import TwilioCallButton from "../components/twilio-call-button";
import { createAction } from "apps/tasks/utils/tasks-actions";
import { useIdentity } from "base/identity-resolver";
import { setTimestampToMidnightAndRemoveMilliseconds } from "apps/journeys/utils/helpers";

const useTwilio = (extension, phoneNumber, contactId) => {
  const [device, setDevice] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const { user, session } = useIdentity();

  useEffect(() => {
    const establishDevice = async () => {
      if (device) return; // don't make a new one

      const twilioDevice = await establishTwilioDevice();
      setDevice(twilioDevice);
    };

    establishDevice(); // create device

    return () => {
      if (device) device.destroy(); // destroy on unmount
    };
  }, [device]);

  const startCall = async () => {
    if (device) {
      const { oid, app_key, token } = session;
      const { id } = user;
      const isValid = validateNumber(phoneNumber, true);
      if (isValid) {
        const call = await device.connect({
          params: {
            oid: String(oid),
            app_key,
            auth: token,
            To: formattedUSPhoneNumber(phoneNumber),
            ext: extension,
          },
        });
        const today = setTimestampToMidnightAndRemoveMilliseconds(new Date().getTime());
        createAction({
          contact_id: contactId,
          date_occurred: today,
          outreach_method: "PHONE",
          oid: oid,
          completed_by_id: id,
          creator_user_id: id,
          updater_user_id: id,
        });

        // event listeners
        call.on("ringing", () => setActiveCall(call));
        call.on("accept", () => setActiveCall(call));
        call.on("disconnect", () => setActiveCall(null));
        call.on("cancel", () => setActiveCall(null));
      } else EverTrue.Alert.error("Invalid Phone Number");
    } else {
      EverTrue.Alert.error("Sorry, there was an issue with our Caller integration. Please try again.");
    }
  };

  const endCall = () => {
    if (device) device.disconnectAll();
    setActiveCall(null);
  };

  const TwilioCallButtonWithContext = () => (
    <TwilioCallButton device={device} activeCall={activeCall} startCall={startCall} endCall={endCall} />
  );

  return {
    TwilioCallButtonWithContext,
    activeCall,
  };
};

export default useTwilio;

//

// IF WE WANT INCOMING CALLS:
// 1. we need to update the outreach service to enable incoming calls (currently disabled).
// 2. Register Device (Only necessary for incoming calls) (twilioDevice.register();)
// 3. add event listener for registered (optional) twilioDevice.on("registered", () => {
//   EverTrue.Alert.success("Your Voice Device is ready to use!");
// });
// 4. add event listener for receiving incoming calls (twilioDevice.on("incoming", handleIncomingCall);)

// IF WE WANT TO ALLOW DIFFERENT AUDIO DEVICES
// twilioDevice.audio.on("deviceChange", updateAllAudioDevices.bind(device));

// // Show audio selection UI if it is supported by the browser.
// if (twilioDevice.audio.isOutputSelectionSupported) {
//   audioSelectionDiv.classList.remove("hide");
// }
